import Vue from "vue";
import VueI18n from 'vue-i18n'
import App from "./App.vue";
import router, {normalizeRoutePath} from "./router";
import store from "./store";
import {VueRecaptcha} from 'vue-recaptcha'
import {logWarn} from '@/helpers';

Vue.config.productionTip = false;
Vue.use(VueI18n);

// Pre-set new design flag before router kicks in
try {
  if (window.appConfig && typeof window.appConfig.frontend_mode !== 'undefined') {
    store.commit('setFrontendMode', window.appConfig.frontend_mode || 'stream');
  }
  if (window.appConfig && typeof window.appConfig.new_design_enabled !== 'undefined') {
    store.commit('setNewDesignEnabled', !!window.appConfig.new_design_enabled);
  }
} catch (e) {
  logWarn('Failed to pre-set new design flag from appConfig', e);
}

// Router guard switches to new design routes when enabled
router.beforeEach((to, from, next) => {
  const mode = store.getters.frontendMode || window.appConfig?.frontend_mode || 'stream';
  const isCheckout = mode === 'checkout';
  const isNew = store.getters.newDesignEnabled || !!window.appConfig?.new_design_enabled;
  const routePath = normalizeRoutePath(to.path);
  if (isCheckout) {
    if (routePath === '/') return next('/checkout');
    if (!routePath.startsWith('/checkout')) return next('/checkout');
    return next();
  }
  if (routePath.startsWith('/checkout')) return next(isNew ? '/wall/home' : '/');
  if (routePath === '/' && isNew) return next('/wall/home');
  if ((routePath === '/subscriptions' || routePath === '/subscriptions/select') && isNew) {
    return next({ path: '/wall/subscription', query: to.query, hash: to.hash });
  }
  if (routePath.startsWith('/wall') && !isNew) return next('/');
  return next();
});

if(window.appConfig?.free_effects_enabled || window.appConfig?.free_generation_enabled) {
  Vue.use(VueRecaptcha, { siteKey: window.appConfig.google_recaptcha_site_key })
}

export const i18n = new VueI18n({
    locale: 'ru',
    messages: {
        en: {
            common: { save: 'Save', saved: 'Saved', cancel: 'Cancel' },
            show_all: 'Show all',
            hide_all: 'Hide',
            date_time_format: 'YYYY/MM/DD',
            logout: 'Logout',
            wall: {
                tabs: { home: 'Home', effects: 'Effects', one_time_donation: 'One-off donation', about: 'About channel', subscription: 'Subscription' },
                home: {
                    welcome_title: 'Welcome to channel {name}.',
                    welcome_subtitle: 'Posts and other materials for sponsors will appear here.',
                    wall_disabled_global: 'Wall functionality is temporarily disabled. Please come back later.',
                    wall_disabled_channel: 'Channel owner has temporarily disabled the wall.',
                    no_posts_yet_hint: 'There are no posts yet. You can support the channel with effects.',
                    no_posts_yet_donation_hint: 'There are no posts yet. You can support the channel with a one-off donation.',
                    please_login_to_view: 'To see posts and sponsor materials, please log in.',
                    not_a_sponsor_cta: 'You are not a sponsor yet. To access exclusive content, please choose a subscription level.',
                    support_with_donation: 'You can support the channel with a one-off donation and send an effect to the stream.',
                    support_with_donation_only: 'You can support the channel with a one-off donation.',
                    login_tile_title: 'Log in to access members-only content',
                    login_tile_text: 'Become a channel sponsor to see exclusive posts and media. You can also buy and gift subscriptions. Log in to continue.',
                },
                live: { status: 'LIVE', send_effect: 'Send effect' },
                post: {
                    locked: 'Locked',
                    unlock: 'Unlock',
                    hidden_text_placeholder: 'Hidden text is available to sponsors.',
                    hidden_attachment_placeholder: 'Hidden attachment is available to sponsors.',
                    sponsors_only_text_locked: 'Hidden text for sponsors',
                    sponsors_only_text_title: 'Text for sponsors',
                    sponsors_only_link_locked: 'Link only for sponsors',
                    text_only_for_sponsors: 'Text only for sponsors'
                },
                subscription: {
                    select_level: 'Choose a level',
                    your_subscription: 'Your subscription',
                    pay: 'Pay',
                    cancel: 'Cancel subscription',
                    public_name_label: 'Public sponsor name (optional)',
                    public_name_placeholder: 'Up to 50 characters',
                    scheduled_upgrade: 'Scheduled upgrade to: {name}',
                    current_level: 'Current',
                    subscribe: 'Subscribe',
                    switch_level: 'Switch',
                    upgrade: 'Upgrade',
                    schedule_downgrade: 'Downgrade',
                    confirm_title: 'You are subscribing to level "{name}"',
                    payment_method: 'Subscription payment method',
                    payment_method_card: 'Internet banking',
                    payment_method_btc: 'Pay with Bitcoin (BTC)',
                    payment_method_xmr: 'Pay with Monero (XMR)',
                    period: 'Period',
                    period_month: '1 month',
                    period_months: '{months} months',
                    discount_badge: '-{percent}%',
                    discount_hint: '-{percent}% for {months}-month payment',
                    per_month: 'Per month',
                    total: 'Total',
                    expires_soon: 'Expires soon (in {days} days)',
                    grant_with_class: '{limit} effects "{class}" {period}',
                    grant_plain: '{limit} effects {period}',
                    grant_period: {
                        hour: 'per hour',
                        day: 'per day',
                        week: 'per week',
                        month: 'per month',
                    },
                    downgrade_crypto_hint: 'Switching to a lower level is only available with internet banking. If you want the level "{name}" and want to pay with cryptocurrency, cancel your current subscription and, after it ends, subscribe to the desired level with crypto.',
                    credit: 'Credit for remaining days',
                    gift_card_title: 'Gift a subscription',
                    gift_card_subtitle: 'Buy one or several gift subscriptions and share activation links with anyone you choose.',
                    gift_note_90_days: 'Gift activation links are valid for 90 days before activation.',
                    gift_button: 'Gift',
                    gift_modal_title: 'Gift subscription purchase',
                    gift_level: 'Subscription level',
                    gift_payment_method: 'Payment method',
                    gift_period: 'Subscription period',
                    gift_quantity: 'Number of gifts',
                    gift_buy_button: 'Buy gifts',
                    gift_links_title: 'Gift activation links',
                    gift_show: 'Show',
                    gift_hide: 'Hide',
                    gift_collapsed_summary: 'You paid for {purchased} gift subscriptions, {notActivated} are not activated yet.',
                    gift_copy_link: 'Copy link',
                    gift_link_copied: 'Copied',
                    gift_link_status_activated: 'Activated',
                    gift_link_status_not_activated: 'Not activated',
                    gift_link_status_expired: 'Expired',
                    gift_link_expires_at: 'Expires on {date}',
                    gift_purchase_pending: 'Payment is being processed. Activation links will appear after payment confirmation.',
                    gift_purchase_failed: 'Payment was not completed. You can try again.',
                    gift_purchase_status_initiated: 'Awaiting payment',
                    gift_purchase_status_awaiting_blockchain: 'Awaiting blockchain confirmation',
                    gift_purchase_status_paid: 'Paid',
                    gift_purchase_status_failed: 'Failed',
                    gift_purchase_status_expired: 'Expired',
                    gift_purchase_status_refunded: 'Refunded',
                    gift_redeem_title: 'Gift subscription activation',
                    gift_redeem_subtitle: 'Sign in with Google to activate this gift subscription on your account.',
                    gift_redeeming: 'Activating your gift subscription...',
                    gift_redeemed_success: 'Gift subscription is activated successfully.',
                    gift_redeem_retry: 'Try activation again',
                    gift_activate_anyway: 'Activate and replace level',
                    gift_go_subscription: 'Go to subscriptions',
                    gift_invalid_link: 'Gift link is invalid.',
                    gift_replace_warning: 'You already have an active subscription level "{current}". After activation it will be replaced with level "{gift}".',
                },
                effects: {
                    hero: {
                        title: 'Send an effect for "{name}" channel',
                        title_donation_only: 'Support "{name}" channel',
                        text_donation_only: 'Choose a payment option and support the channel with a one-off donation.',
                        text_guest: 'Sign in to send an effect to the stream by subscribing, or send an effect with a one-off donation to support the channel.',
                        text_sponsor: 'Thank you for supporting the channel! Trigger a new effect or use AI stickers.',
                        text_supporter: 'Pick a payment option or use your membership perks to send an effect.',
                        live_hint: 'Stream is live — effects will play instantly after moderation.',
                        subscription_cta: 'View membership options',
                        free_hint: 'Daily free effects and AI attempts are available while they last.',
                    },
                    mode: {
                        title: 'Choose how you want to send an effect',
                        title_donation_only: 'Choose how you want to donate',
                        subtitle: 'Select one of the options below and complete the effect form.',
                        subtitle_donation_only: 'Select a payment option below and enter the donation amount.',
                        subscription_subtitle: 'Use membership benefits, free limits and bonuses.',
                        badge_active: 'Active',
                        badge_members: 'Members',
                        badge_free: 'Free',
                        free_subtitle: 'Captcha protected — send once the timer reaches zero.',
                        donation_subtitle: 'Secure one-off payment via bank card or wallet.',
                        BTC_subtitle: 'Pay with Bitcoin using a generated address.',
                        XMR_subtitle: 'Pay with Monero using a generated address.',
                    },
                    sidebar: {
                        subscription_title: 'Your membership',
                        subscription_level: 'Level: {level}',
                        subscription_paid_till: 'Paid until {date}',
                        manage_subscription: 'Manage subscription',
                        free_limits: 'Free sends available today',
                        limit_entry: '{name}: {count}',
                        ai_title: 'AI stickers',
                        ai_left: '{count} free attempts left',
                        ai_empty: 'No free attempts left for today.',
                        next_free: 'Next free effect',
                        free_ready: 'You can send a free effect right now.',
                        free_in: 'Available in {seconds} seconds.',
                    },
                    effects: {
                        preview: 'Preview',
                        close_preview: 'Close preview',
                        min_donation: 'From {amount} {currency}',
                        badge_preset: 'effect generation',
                    },
                }
            },
            periods: {
                day: 'day',
                week: 'week',
                month: 'month',
                annual: 'annual',
            },
            index: {
                login_header: 'Sign in with YouTube to get to the list of effects',
                login_comment: 'To verify that you are a sponsor of the channel, we need access to this information in your YouTube account.',
                login_prompt_youtube: 'Sign in with YouTube',
                login_prompt_google: 'Sign in with Google',
                service_footer: 'The service is provided by <a href="https://digitalduck.live" target="_blank" class="orange-link">Digital Duck</a>.<br>By using this service you agree to the <a href="https://digitalduck.live/privacy" target="_blank" class="orange-link">Digital Duck privacy policy</a>, <a href="https://www.youtube.com/t/terms" target="_blank" class="orange-link">YouTube\'s Terms of Service</a> and <a href="http://www.google.com/policies/privacy" target="_blank" class="orange-link">Google\'s Privacy Policy</a>',
            },
            sponsor: {
                limit_prefix: 'At your membership level, you can send for free:',
                no_effects: 'You are out of free effects. Check out other available <a href="{href}" class="orange-link">sponsorship levels</a>',
                no_active_subscription: 'You have no active subscription. Check out other available <a href="{href}" class="orange-link">sponsorship levels</a> and subscribe.',
                no_effects_without_url: 'You are out of free effects. Check out other available sponsorship levels',
                no_membership: 'To send effects to the livestream for free, <a href="{href}" class="orange-link">become a sponsor of the channel</a>, and then refresh this page',
                no_membership_without_url: 'To send effects to the livestream for free, become a sponsor of the channel, and then refresh this page',
                no_subscription: 'You are signing up for a recurrent donation to get bonuses according to the level:',
                per_hour: 'per hour',
                per_day: 'per day',
                per_week: 'per week',
                per_month: 'per month',
                ai_sticker_creation: 'AI sticker creation',
                select_level: 'Select your level',
            },
            forms: {
                your_name: 'Your name - it will appear in the stream with the effect',
                name_unavailable: 'Name in the selected effect is unavailable',
                symbol_limit: 'Symbol limit',
                your_message: 'Optional message - it will appear in the stream with the effect',
                post_effect: 'Send effect',
                create_sticker: 'Create sticker',
                payment_mode_donation: 'One-off donation (Internet banking)',
                payment_mode_BTC: 'One-off donation (Bitcoin payment)',
                payment_mode_XMR: 'One-off donation (Monero payment)',
                payment_mode_subscription: 'Recurrent payments',
                select_effect: 'Select an effect',
                initiate_payment: 'Initiate payment',
                start_new_payment: 'Start a new payment',
                incomplete_payment_for_sticker_creation_found: 'Incomplete payment for sticker creation found, ',
                you_can_return_to_payment_form: 'you can return to the payment form.',
                completed_payment_for_sticker_creation_found: 'Completed payment for sticker creation found, ',
                free_session_for_sticker_creation_found: 'Incomplete session for sticker creation found, ',
                you_can_select_one_of_the_stickers: 'you can select one of the created stickers to send to the stream.',
                you_can_create_a_sticker: 'you can create a sticker.',
                free_effects_default_payment_option_name: 'Send effect for free',
                next_free_effect_available: 'Next free effect will be available in {seconds} seconds',
            },
            crypto_payment: {
                transfer_using_the_following_details: 'Transfer {currency} using the following details',
                amount: 'Minimum amount (for extra safety, we recommend to send a slightly larger amount)',
                amount_copied: 'amount copied',
                to_the_address: 'To the address',
                address_copied: 'address copied',
                if_you_have_the_wallet_app_installed: 'If you have the {currency} wallet app installed',
                pay_in_the_app: 'Pay in the app',
                your_effect_will_appear_on_the_stream_after_one_confirmation: 'Your effect will appear on the stream after one confirmation. Typically, this takes 5-10 minutes (or longer, depending on network congestion and the commission you\'ve set).',
                awaiting_payment: 'Awaiting payment',
                awaiting_confirmation: 'Payment found. Awaiting confirmation on the blockchain',
                go_back: 'Go back',
                we_will_redirect_you_to_the_sticker_creation_page: 'You will be redirected to the <a href="{href}" class="orange-link">sticker creation page</a> after one confirmation. Typically, this takes 5-10 minutes (or longer, depending on network congestion and the commission you\'ve set).',
            },
            crypto_subscription: {
                transfer_using_the_following_details: 'Pay your subscription for level "{name}" using the details below',
                subtitle: 'After payment confirmation your membership will be activated/extended automatically.',
                description: 'Payment on the blockchain may take several minutes after it is sent.',
                pending_upgrade: 'Payment for upgrade to "{name}" is not finished. Time left: {time}',
                cancel_payment: 'Cancel payment',
                go_back: 'Change payment method',
                time_hours_short: 'h',
                time_minutes_short: 'm',
            },
            checkout: {
                donation_label: 'Donation',
                support_title: 'Support organization',
                default_description: 'Choose an amount and payment method. A one-off payment can be paid by card or available cryptocurrency.',
                socials_label: 'Social links',
                payment_type_label: 'Payment type',
                one_time: 'One-off',
                monthly: 'Monthly',
                current_monthly_title: 'Monthly payment is already active',
                current_monthly_text: 'You can change or cancel it in monthly payment management.',
                amount: 'Amount',
                min_amount: 'Minimum {amount} {currency}',
                payment_method: 'Payment method',
                monthly_login_text: 'Sign in with Google to set up a monthly payment. You will be able to cancel it later.',
                logged_in_google: 'You are signed in with Google.',
                manage_monthly_payment: 'Manage monthly payment',
                card: 'Card',
                going_to_payment: 'Redirecting to payment...',
                submit_monthly: 'Set up monthly payment',
                submit_donation: 'Donate',
                min_amount_error: 'Minimum amount is {amount} {currency}.',
                max_amount_error: 'Maximum amount is {amount} {currency}.',
                payment_start_error: 'Could not start payment. Please try again.',
                success: {
                    title: 'Thank you for your support',
                    text: 'Payment accepted. You can return to the donation page or close this tab.',
                    back: 'Return',
                },
                subscription: {
                    back: 'Back to donation',
                    title: 'Monthly payment',
                    login_title: 'Sign in with Google',
                    login_text: 'After signing in, you will be able to view the monthly payment status and cancel it.',
                    status: 'Status',
                    active: 'Active',
                    paid_until: 'Paid until',
                    next_charge: 'Next charge',
                    canceling: 'Canceling...',
                    cancel_monthly: 'Cancel monthly payment',
                    processing_title: 'Payment is processing',
                    processing_text: 'We will update the status after payment confirmation. This usually takes a few seconds.',
                    refresh_status: 'Refresh status',
                    not_found_title: 'Monthly payment was not found',
                    not_found_text: 'You can set up a new monthly payment on the donation page.',
                    start_payment: 'Set up payment',
                    cancel_error: 'Could not cancel payment. Please try again.',
                },
                crypto: {
                    transfer_title: 'Transfer {currency} using the following details',
                    description: 'Your donation will be counted after the payment is confirmed on the network.',
                    amount: 'Amount',
                    amount_copied: 'Amount copied',
                    address: 'Address',
                    address_copied: 'Address copied',
                    qr_code: 'QR code',
                    wallet_hint: 'If you have the {currency} wallet app installed',
                    pay_in_app: 'Pay in the app',
                    awaiting_confirmation: 'Awaiting confirmation',
                    awaiting_payment: 'Awaiting payment',
                },
            },
            sent: {
                done: 'Done! Your effect will show up on the stream soon',
                go_back: 'Go back to the effects',
            },
            success: {
                thank_you_for_your_donation: 'Thank you for your donation!',
                your_effect_will_appear_on_the_stream_within_a_minute: 'Your effect will appear on the stream within a minute',
                go_back: 'Go back',
            },
            error: {
                page_under_construction: 'Page under construction',
                error_while_unsubscribing: 'An error occurred while unsubscribing. Error code: {code}. Please try again in a few minutes.',
                error_while_subscribing: 'An error occurred while subscribing. Error code: {code}. Please try again in a few minutes.',
                error_while_payment: 'An error occurred while payment. Please try again in a few minutes.',
                error_while_checking_sticker_creation_session_please_reload: 'An error occurred while checking the sticker creation session. Please reload the page.',
            },
            subscriptions: {
                subscriptions_disabled: 'Subscriptions are temporarily disabled.',
                scheduled_transition: 'Scheduled transition',
                period_day: 'DAY',
                period_week: 'WK',
                period_month: 'MO',
                period_annual: 'YR',
                no_subscription: 'You don\'t have a subscription, but you can subscribe right now.',
                select_level: 'Select level',
                select_your_level: 'Select your subscription level',
                this_is_your_current_level: 'This is your current level.',
                available_at_this_level: 'Available at this level: ',
                subscribe: 'Subscribe',
                switch_level: 'Switch',
                schedule_transition: 'Schedule transition',
                go_back: 'Go back to the subscriptions',
                unsubscribe_header: 'Subscription cancellation',
                your_subscriptions: 'Your subscription',
                your_current_level: 'You subscribed to the "{name}" level for {currency} {price}/{period}.',
                your_expired_level: 'Previously, you were subscribed to the "{name}" level for {price} {currency}/{period}, but we were unable to renew your subscription in time.',
                paid_till: 'Paid till ',
                unsubscribe: 'Unsubscribe',
                next_level: 'You have scheduled a transition to the "{name}" level for {currency} {price}/{period} from {date}.',
                you_have_canceled_this_subscription: 'You have canceled this subscription; it remains active until the end of the paid period.',
                paid_with_crypto_non_recurrent: 'Subscription is paid with cryptocurrency. We will not be able to charge the next payment automatically, so after the end of the paid period you will be able to purchase a new subscription. To move to a lower level, you will also need to wait until the current subscription expires.',
                paid_as_gift_non_recurrent: 'This subscription was activated by a gift link, so it will not renew automatically after the paid period ends.',
                continue_subscription: 'Continue subscription',
                you_can_subscribe_again: 'You can subscribe to this level again, and the next payment will be made on {date}.',
                change_level: 'Change level',
                subscribe_again: 'Subscribe again',
                payment_in_progress_please_wait: 'Payment in progress, please wait...',
                after_the_transaction_you_will_be_automatically_redirected: 'After the transaction you will be automatically redirected to the page with the effects.',
                awaiting_payment: 'Awaiting payment',
                payment_success_redirecting: 'Payment successful, you will be redirected to the page with the effects shortly.',
                payment_failed_try_again: 'Payment failed, please try again.',
                try_again: 'Try again',
                this_level_is_higher_than_your_current_one: 'You have an active subscription to the level "{name}" level, but you can switch to this level right now.',
                this_level_is_lower_than_your_current_one: 'This level is lower than your current one. You can schedule a transition to this level. The transition will be made from {date}, and until then you will continue to use your current level.',
                subscribe_to_this_level_now: 'Subscribe to this level now.',
                this_level_is_next: 'This level is next in line. You have scheduled a transition to this level from {date}.',
                you_have_planned_the_transition_to_the_level: 'You have scheduled a transition to the level "{name}", but you can cancel it.',
                cancel_transition: 'Cancel transition',
                are_you_sure_you_want_to_unsubscribe_from_level: 'Are you sure you want to unsubscribe from level "{name}"? If canceled, the charges will stop and the subscription will be deleted after the end of the paid period ({date}).' ,
                the_transition_will_also_be_canceled: 'Previously, you scheduled the transition to the "{name}" level, if you cancel the subscription, the transition will also be canceled.',
                payment_method_failed_subscribe_again: 'An error occurred while trying to charge the last payment. Select a sponsorship level and try again, or select another payment method.',
                is_expired: 'The subscription has expired: ',
                settings: 'Subscription settings',
                free_upgrade_without_payment: 'This upgrade is fully covered by your existing paid period, so it will be applied without additional payment. This type of free upgrade is available only once for the already paid period.',
                free_upgrade_period: 'Resulting paid period at the new level: {months} months {days} days.',
                credit_once_hint: 'Upgrade credit for unused time is applied once per current paid period. After this upgrade, only this payment will be used for future credit; for the next upgrade the discount for this period will not exceed {amount} {currency}.',
            },
            generation: {
              page_header: 'Sticker creation using neural network',
              select_style: 'Select style',
              select_button: 'Select',
              image_style: 'Image style',
              enter_text_here: 'Enter text here',
              enter_text_to_create_sticker: 'Describe what you want to see on the sticker (people, objects, characters, emotions, colors, environment, etc.).',
              create_button: 'Create',
              loading_please_wait: 'Loading, please wait...',
              attempt: 'Attempt:',
              select_this_sticker: 'Select this sticker',
              create_new_sticker: 'Create new sticker',
              style: 'Style',
              text_for_creation: 'Text for creation',
              you_can_select_this_sticker_and_send_it_to_the_stream_right_now: 'You can select this sticker and send it to the stream right now.',
              on_the_next_step_you_can_enter_your_name_and_message_text_if_you_wish: 'On the next step, you can enter your name and message text if you wish.',
              on_the_next_step_you_can_enter_your_name_if_you_wish: 'On the next step, you can enter your name if you wish.',
              on_the_next_step_you_can_enter_your_message_text_if_you_wish: 'On the next step, you can enter your message text if you wish.',
              send_this_sticker: 'Send this sticker',
              back_to_creation: 'Back to sticker creation',
              styles: {
                none: 'No Style',
                enhance: 'Enhance',
                anime: 'Anime',
                photographic: 'Photographic',
                digital_art: 'Digital Art',
                comic_book: 'Comic Book',
                fantasy_art: 'Fantasy Art',
                line_art: 'Line Art',
                analog_film: 'Analog Film',
                neon_punk: 'Neon Punk',
                isometric: 'Isometric',
                low_poly: 'Low Poly',
                origami: 'Origami',
                modeling_compound: 'Modeling Compound',
                cinematic: 'Cinematic',
                three_d_model: '3D Model',
                pixel_art: 'Pixel Art',
                tile_texture: 'Tile Texture'
              },
              creation_in_progress: 'Sticker creation in progress, please wait...',
              error_while_creating_sticker: 'An error occurred while creating the sticker. Please try again.',
              image_not_available_try_to_reload_page: 'Image not available. Please try to reload the page.',
              an_error_occurred_while_creating_the_sticker: 'An error occurred while creating this sticker. Please try again.',
              banned_words_found: 'Banned characters or words were found in the image description. Please try another description.',
              an_unrecognized_error_occurred_while_creating_the_sticker: 'An unrecognized error occurred while creating the sticker. The image is not available. Please try to reload the page.',
              this_attempt_to_create_a_sticker_failed_try_again: 'This attempt to create a sticker failed. Please try again.',
              error_while_posting_sticker: 'An error occurred while posting the sticker. Please try again.',
              send_donation_without_sticker: 'Donate without posting a sticker',
              generation_disabled_globally: 'Sticker creation is temporarily disabled.',
            },
        },
        ru: {
            common: { save: 'Сохранить', saved: 'Сохранено', cancel: 'Отмена' },
            show_all: 'Показать всё',
            hide_all: 'Скрыть',
            date_time_format: 'DD.MM.YYYY',
            logout: 'Выйти',
            wall: {
                tabs: { home: 'Главная', effects: 'Эффекты', one_time_donation: 'Разовое пожертвование', about: 'О канале', subscription: 'Подписка' },
                home: {
                    welcome_title: 'Приветствуем на канале {name}.',
                    welcome_subtitle: 'Здесь будут появляться посты и иные материалы для спонсоров.',
                    wall_disabled_global: 'Функционал стены временно отключен, зайдите позже.',
                    wall_disabled_channel: 'Владелец канала временно отключил функционал стены.',
                    no_posts_yet_hint: 'Пока что здесь нет постов, но вы можете поддержать канал с помощью эффектов.',
                    no_posts_yet_donation_hint: 'Пока что здесь нет постов, но вы можете поддержать канал разовым донатом.',
                    please_login_to_view: 'Чтобы увидеть посты и материалы для спонсоров, пожалуйста, войдите в систему.',
                    not_a_sponsor_cta: 'Вы пока не спонсор канала. Чтобы получить доступ к эксклюзивным материалам, пожалуйста, выберите уровень подписки.',
                    support_with_donation: 'Вы также можете поддержать канал разовым донатом с отправкой эффекта на стрим.',
                    support_with_donation_only: 'Вы также можете поддержать канал разовым донатом.',
                    login_tile_title: 'Войдите, чтобы получить доступ к закрытым материалам',
                    login_tile_text: 'Станьте спонсором канала, чтобы видеть эксклюзивные посты и медиа. Также вы можете покупать и дарить подписки. Войдите, чтобы продолжить.',
                },
                live: { status: 'В ЭФИРЕ', send_effect: 'Отправить эффект' },
                post: {
                    locked: 'Заблокировано',
                    unlock: 'Разблокировать',
                    hidden_text_placeholder: 'Скрытый текст доступен спонсорам.',
                    hidden_attachment_placeholder: 'Скрытое вложение доступно спонсорам.',
                    sponsors_only_text_locked: 'Скрытый текст для спонсоров',
                    sponsors_only_text_title: 'Текст для спонсоров',
                    sponsors_only_link_locked: 'Ссылка только для спонсоров',
                    text_only_for_sponsors: 'Текст только для спонсоров'
                },
                subscription: {
                    select_level: 'Выберите уровень',
                    your_subscription: 'Ваша подписка',
                    pay: 'Оплатить',
                    cancel: 'Отменить подписку',
                    public_name_label: 'Публичное имя спонсора (по желанию)',
                    public_name_placeholder: 'До 50 символов',
                    scheduled_upgrade: 'Запланирован переход на уровень: {name}',
                    current_level: 'Текущий',
                    subscribe: 'Подписаться',
                    switch_level: 'Перейти',
                    upgrade: 'Повысить',
                    schedule_downgrade: 'Понизить',
                    confirm_title: 'Вы подписываетесь на уровень "{name}"',
                    payment_method: 'Способ оплаты подписки',
                    payment_method_card: 'Оплата через интернет-банкинг',
                    payment_method_btc: 'Оплата биткоинами (BTC)',
                    payment_method_xmr: 'Оплата монеро (XMR)',
                    period: 'Период',
                    period_month: '1 месяц',
                    period_months: '{months} мес.',
                    discount_badge: '-{percent}%',
                    discount_hint: 'Скидка {percent}% при оплате за {months} мес.',
                    per_month: 'В месяц',
                    total: 'Итого',
                    expires_soon: 'Подписка заканчивается через {days} дн.',
                    grant_with_class: '{limit} эффект(ов) «{class}» {period}',
                    grant_plain: '{limit} эффект(ов) {period}',
                    grant_period: {
                        hour: 'в час',
                        day: 'в день',
                        week: 'в неделю',
                        month: 'в месяц',
                    },
                    downgrade_crypto_hint: 'Запланировать переход на уровень ниже можно только при оплате через интернет-банкинг. Если вы хотите выбрать уровень спонсорства «{name}» и оплачивать его криптовалютой, отмените текущую подписку и, после её окончания, оформите желаемый уровень с оплатой в криптовалюте.',
                    credit: 'Вычет за оставшиеся дни',
                    free_upgrade_without_payment: 'Этот апгрейд полностью покрывается уже оплаченной подпиской, поэтому он будет выполнен без дополнительной оплаты. Такой бесплатный апгрейд доступен только один раз для уже оплаченного периода.',
                    free_upgrade_period: 'Новый оплаченный период на этом уровне: {months} мес. {days} дн.',
                    credit_once_hint: 'Кредит за уже оплаченный период применяется один раз. После этого апгрейда прошлые платежи в вычет не попадут; при следующем апгрейде вычет не превысит {amount} {currency}.',
                    gift_card_title: 'Подарить подписку',
                    gift_card_subtitle: 'Купите один или несколько подарков и отправьте ссылки активации тем, кому хотите.',
                    gift_note_90_days: 'Подарочные ссылки действуют 90 дней до момента активации.',
                    gift_button: 'Подарить',
                    gift_modal_title: 'Покупка подарочных подписок',
                    gift_level: 'Уровень подписки',
                    gift_payment_method: 'Способ оплаты',
                    gift_period: 'Период подписки',
                    gift_quantity: 'Количество подарков',
                    gift_buy_button: 'Купить подарки',
                    gift_links_title: 'Ссылки активации подарков',
                    gift_show: 'Показать',
                    gift_hide: 'Скрыть',
                    gift_collapsed_summary: 'У вас оплачено {purchased} подарочных подписок, из них не активировано {notActivated} шт.',
                    gift_copy_link: 'Копировать ссылку',
                    gift_link_copied: 'Скопировано',
                    gift_link_status_activated: 'Активирована',
                    gift_link_status_not_activated: 'Не активирована',
                    gift_link_status_expired: 'Истекла',
                    gift_link_expires_at: 'Действует до {date}',
                    gift_purchase_pending: 'Платеж обрабатывается. Ссылки активации появятся после подтверждения оплаты.',
                    gift_purchase_failed: 'Оплата не завершена. Попробуйте снова.',
                    gift_purchase_status_initiated: 'Ожидание оплаты',
                    gift_purchase_status_awaiting_blockchain: 'Ожидание подтверждения в блокчейне',
                    gift_purchase_status_paid: 'Оплачено',
                    gift_purchase_status_failed: 'Ошибка оплаты',
                    gift_purchase_status_expired: 'Истекло',
                    gift_purchase_status_refunded: 'Возврат',
                    gift_redeem_title: 'Активация подарочной подписки',
                    gift_redeem_subtitle: 'Войдите через Google, чтобы активировать подарочную подписку на свой аккаунт.',
                    gift_redeeming: 'Активируем подарочную подписку...',
                    gift_redeemed_success: 'Подарочная подписка успешно активирована.',
                    gift_redeem_retry: 'Повторить активацию',
                    gift_activate_anyway: 'Активировать и заменить уровень',
                    gift_go_subscription: 'Перейти к подпискам',
                    gift_invalid_link: 'Некорректная подарочная ссылка.',
                    gift_replace_warning: 'У вас уже есть активная подписка уровня «{current}». После активации она будет заменена на уровень «{gift}».',
                },
                effects: {
                    hero: {
                        title: 'Отправьте эффект для канала "{name}"',
                        title_donation_only: 'Поддержать канал "{name}"',
                        text_donation_only: 'Выберите удобный способ оплаты и поддержите канал разовым донатом.',
                        text_guest: 'Войдите, чтобы поддержать канал и отправить эффект на стрим за подписку, либо отправьте эффект за разовое пожертвование.',
                        text_sponsor: 'Вы уже поддерживаете канал — используйте лимиты или создайте новый эффект за разовое пожертвование.',
                        text_supporter: 'Выберите удобный способ оплаты или подключите подписку, чтобы отправить эффект.',
                        live_hint: 'Стрим в эфире — эффект сработает сразу после модерации.',
                        subscription_cta: 'Выбрать подписку',
                        free_hint: 'Доступны бесплатные эффекты и попытки ИИ — успейте воспользоваться.',
                    },
                    mode: {
                        title: 'Выберите способ отправки эффекта',
                        title_donation_only: 'Выберите способ доната',
                        subtitle: 'Выберите один из способов ниже и заполните форму эффекта.',
                        subtitle_donation_only: 'Выберите способ оплаты ниже и укажите сумму доната.',
                        subscription_subtitle: 'Лимиты и бонусы доступны для подписчиков.',
                        badge_active: 'Активна',
                        badge_members: 'Подписка',
                        badge_free: 'Бесплатно',
                        free_subtitle: 'Когда таймер обнулится, можно отправить без оплаты.',
                        donation_subtitle: 'Мгновенный платёж с банковской карты.',
                        BTC_subtitle: 'Оплата эффектов в биткоине.',
                        XMR_subtitle: 'Оплата эффектов в монеро.',
                    },
                    sidebar: {
                        subscription_title: 'Ваша подписка',
                        subscription_level: 'Уровень: {level}',
                        subscription_paid_till: 'Оплачено до {date}',
                        manage_subscription: 'Управлять подпиской',
                        free_limits: 'Бесплатные эффекты сегодня',
                        limit_entry: '{name}: {count}',
                        ai_title: 'AI-стикеры',
                        ai_left: 'Осталось {count} бесплатных попыток',
                        ai_empty: 'На сегодня бесплатных попыток не осталось.',
                        next_free: 'Следующий бесплатный эффект',
                        free_ready: 'Можно отправить бесплатный эффект прямо сейчас.',
                        free_in: 'Можно отправить через {seconds} сек.',
                    },
                    effects: {
                        preview: 'Превью',
                        close_preview: 'Закрыть превью',
                        min_donation: 'от {amount} {currency}',
                        badge_preset: 'генерация эффектов',
                    },
                }
            },
            periods: {
                day: 'день',
                week: 'нед',
                month: 'мес',
                annual: 'год',
            },
            index: {
                login_header: 'Войдите с помощью YouTube, чтобы попасть на страницу эффектов',
                login_comment: 'Мы должны убедиться, что вы являетесь спонсором канала на YouTube.',
                login_prompt_youtube: 'Войти через YouTube',
                login_prompt_google: 'Войти через Google',
                service_footer: 'Используя этот сайт, вы соглашаетесь с <a href="https://digitalduck.live/privacy" target="_blank" class="orange-link">политикой конфиденциальности Digital Duck</a>, <a href="https://www.youtube.com/t/terms" target="_blank" class="orange-link">Правилами и условиями использования YouTube</a> и <a href="http://www.google.com/policies/privacy" target="_blank" class="orange-link">Политикой Конфиденциальности Google</a>. Услуга предоставляется сервисом <a href="https://digitalduck.live" target="_blank" class="orange-link">Digital Duck</a>.',
            },
            sponsor: {
                limit_prefix: 'На вашем уровне спонсорства вы можете бесплатно отправить ещё:',
                no_effects: 'У вас кончились бесплатные эффекты. Посмотрите, какие <a href="{href}" class="orange-link">уровни спонсорства</a> доступны ещё.',
                no_active_subscription: 'У вас отсутствует активная подписка. Посмотрите, какие <a href="{href}" class="orange-link">уровни спонсорства</a> доступны и подпишитесь.',
                no_effects_without_url: 'У вас кончились бесплатные эффекты. Посмотрите на канале, какие уровни спонсорства доступны ещё.',
                no_membership: 'Чтобы получить возможность отправлять эффекты в трансляцию бесплатно, <a href="{href}" class="orange-link">станьте спонсором канала</a>, а потом обновите эту страницу',
                no_membership_without_url: 'Чтобы получить возможность отправлять эффекты в трансляцию бесплатно, станьте спонсором канала, а потом обновите эту страницу',
                no_subscription: 'Вы подписываетесь на регулярное пожертвование и получаете бонусы в зависимости от уровня:',
                per_hour: 'в час',
                per_day: 'в день',
                per_week: 'в неделю',
                per_month: 'в месяц',
                ai_sticker_creation: 'Создание AI-стикеров',
                select_level: 'Выбрать свой уровень',
            },
            forms: {
                your_name: 'Ваше имя — оно появится в трансляции вместе с эффектом',
                name_unavailable: 'В выбранном эффекте имя недоступно',
                symbol_limit: 'Ограничение символов',
                your_message: 'Если хотите, введите сообщение — оно появится в трансляции вместе с эффектом',
                post_effect: 'Отправить эффект',
                create_sticker: 'Создать стикер',
                payment_mode_donation: 'Разовое пожертвование (интернет-банкинг)',
                payment_mode_BTC: 'Разовое пожертвование (платеж биткоинами)',
                payment_mode_XMR: 'Разовое пожертвование (платеж монеро)',
                payment_mode_subscription: 'Регулярное пожертвование',
                select_effect: 'Выбор эффекта',
                initiate_payment: 'Перейти к оплате',
                start_new_payment: 'Начать новую оплату',
                incomplete_payment_for_sticker_creation_found: 'Найден незавершенный платеж за создание стикера, ',
                you_can_return_to_payment_form: 'вы можете вернуться к форме оплаты.',
                completed_payment_for_sticker_creation_found: 'Найден платеж за создание стикера, ',
                free_session_for_sticker_creation_found: 'Найдена сессия для создания стикера, ',
                you_can_select_one_of_the_stickers: 'вы можете выбрать один из созданных стикеров для отправки в эфир.',
                you_can_create_a_sticker: 'вы можете создать стикер.',
                free_effects_default_payment_option_name: 'Отправить эффект бесплатно',
                next_free_effect_available: 'Следующий бесплатный эффект будет доступен через {seconds} сек.',
            },
            crypto_payment: {
                transfer_using_the_following_details: 'Переведите {currency} по следующим реквизитам',
                amount: 'Минимальная сумма (в целях дополнительной безопасности, рекомендуем отправить сумму немного больше)',
                amount_copied: 'сумма скопирована',
                to_the_address: 'На адрес',
                address_copied: 'адрес скопирован',
                if_you_have_the_wallet_app_installed: 'Eсли у вас установлено приложение кошелька {currency}',
                pay_in_the_app: 'Оплатить в приложении',
                your_effect_will_appear_on_the_stream_after_one_confirmation: 'Ваш эффект появится на стриме после одного подтверждения. Как правило, это занимает 5-10 минут (или выше, в зависимости от загруженности сети и выставленной вами комиссии).',
                awaiting_payment: 'Ожидание оплаты',
                awaiting_confirmation: 'Платеж обнаружен. Ожидание подтверждения в блокчейне',
                go_back: 'Возврат',
                we_will_redirect_you_to_the_sticker_creation_page: 'Вы перейдёте на <a href="{href}" class="orange-link">страницу создания стикера</a> после одного подтверждения. Как правило, это занимает 5-10 минут (или выше, в зависимости от загруженности сети и выставленной вами комиссии).',
            },
            crypto_subscription: {
                transfer_using_the_following_details: 'Оплатите подписку на уровень «{name}» по указанным реквизитам',
                subtitle: 'После подтверждения оплаты ваша подписка будет активирована/продлена автоматически.',
                description: 'Подтверждение в блокчейне может занять несколько минут после отправки.',
                pending_upgrade: 'Не завершён платеж за уровень «{name}». Осталось времени: {time}',
                cancel_payment: 'Отменить платеж',
                go_back: 'Сменить способ оплаты',
                time_hours_short: 'ч',
                time_minutes_short: 'м',
            },
            checkout: {
                donation_label: 'Пожертвование',
                support_title: 'Поддержать организацию',
                default_description: 'Выберите сумму и способ оплаты. Разовый платеж можно оплатить картой или доступной криптовалютой.',
                socials_label: 'Социальные сети',
                payment_type_label: 'Тип платежа',
                one_time: 'Разово',
                monthly: 'Ежемесячно',
                current_monthly_title: 'Ежемесячный платеж уже оформлен',
                current_monthly_text: 'Изменить или отменить его можно в управлении ежемесячным платежом.',
                amount: 'Сумма',
                min_amount: 'Минимум {amount} {currency}',
                payment_method: 'Способ оплаты',
                monthly_login_text: 'Для ежемесячного платежа войдите через Google. Так вы сможете отменить платеж позже.',
                logged_in_google: 'Вы вошли через Google.',
                manage_monthly_payment: 'Управление ежемесячным платежом',
                card: 'Карта',
                going_to_payment: 'Переходим к оплате...',
                submit_monthly: 'Оформить ежемесячный платеж',
                submit_donation: 'Пожертвовать',
                min_amount_error: 'Минимальная сумма - {amount} {currency}.',
                max_amount_error: 'Максимальная сумма - {amount} {currency}.',
                payment_start_error: 'Не удалось начать оплату. Попробуйте еще раз.',
                success: {
                    title: 'Спасибо за поддержку',
                    text: 'Платеж принят. Вы можете вернуться на страницу пожертвования или закрыть эту вкладку.',
                    back: 'Вернуться',
                },
                subscription: {
                    back: 'Назад к пожертвованию',
                    title: 'Ежемесячный платеж',
                    login_title: 'Войдите через Google',
                    login_text: 'После входа вы сможете посмотреть статус ежемесячного платежа и отменить его.',
                    status: 'Статус',
                    active: 'Активен',
                    paid_until: 'Оплачен до',
                    next_charge: 'Следующее списание',
                    canceling: 'Отменяем...',
                    cancel_monthly: 'Отменить ежемесячный платеж',
                    processing_title: 'Платеж обрабатывается',
                    processing_text: 'Мы обновим статус после подтверждения платежа. Обычно это занимает несколько секунд.',
                    refresh_status: 'Обновить статус',
                    not_found_title: 'Ежемесячный платеж не найден',
                    not_found_text: 'Вы можете оформить новый ежемесячный платеж на странице пожертвования.',
                    start_payment: 'Оформить платеж',
                    cancel_error: 'Не удалось отменить платеж. Попробуйте еще раз.',
                },
                crypto: {
                    transfer_title: 'Переведите {currency} по следующим реквизитам',
                    description: 'Ваше пожертвование будет засчитано после подтверждения платежа в сети.',
                    amount: 'Сумма',
                    amount_copied: 'Сумма скопирована',
                    address: 'Адрес',
                    address_copied: 'Адрес скопирован',
                    qr_code: 'QR-код',
                    wallet_hint: 'Если у вас установлено приложение кошелька {currency}',
                    pay_in_app: 'Оплатить в приложении',
                    awaiting_confirmation: 'Ожидание подтверждения',
                    awaiting_payment: 'Ожидание оплаты',
                },
            },
            sent: {
                done: 'Готово! Сейчас ваш эффект появится на стриме',
                go_back: 'Вернуться к эффектам',
            },
            success: {
                thank_you_for_your_donation: 'Спасибо за пожертвование!',
                your_effect_will_appear_on_the_stream_within_a_minute: 'Ваш эффект появится на стриме в течение минуты',
                go_back: 'Возврат',
            },
            error: {
                page_under_construction: 'Страница в разработке',
                error_while_unsubscribing: 'Произошла ошибка при попытке отмены подписки. Код ошибки: {code}. Попробуйте ещё раз через несколько минут.',
                error_while_subscribing: 'Произошла ошибка при попытке подписаться. Код ошибки: {code}. Попробуйте ещё раз через несколько минут.',
                error_while_payment: 'Произошла ошибка при попытке оплатить. Попробуйте ещё раз через несколько минут.',
                error_while_checking_sticker_creation_session_please_reload: 'Произошла ошибка при проверке сессии создания стикера. Пожалуйста, перезагрузите страницу.',
            },
            subscriptions: {
                subscriptions_disabled: 'Подписки временно отключены.',
                scheduled_transition: 'Запланирован переход',
                period_day: 'ДЕНЬ',
                period_week: 'НЕД',
                period_month: 'МЕС',
                period_annual: 'ГОД',
                no_subscription: 'У вас отсутствует подписка, но вы можете подписаться прямо сейчас.',
                select_level: 'Выбрать уровень',
                select_your_level: 'Выберите уровень подписки',
                this_is_your_current_level: 'Это ваш текущий уровень.',
                available_at_this_level: 'На этом уровне доступно: ',
                subscribe: 'Подписаться',
                switch_level: 'Перейти',
                schedule_transition: 'Запланировать переход',
                go_back: 'Возврат к подпискам',
                unsubscribe_header: 'Отмена подписки',
                your_subscriptions: 'Ваша подписка',
                your_current_level: 'Вы подписаны на уровень "{name}" за {price} {currency}/{period}.',
                your_expired_level: 'Ранее вы были подписаны на уровень "{name}" за {price} {currency}/{period}, однако мы не смогли продлить вашу подписку.',
                paid_till: 'Подписка оплачена до ',
                unsubscribe: 'Отменить подписку',
                next_level: 'Вы запланировали переход на уровень "{name}" за {price} {currency}/{period} с {date}.',
                you_have_canceled_this_subscription: 'Вы отменили эту подписку; она будет активна до конца оплаченного периода.',
                paid_with_crypto_non_recurrent: 'Подписка оплачена криптовалютой. Мы не сможем списать очередной платёж, поэтому после окончания оплаченного периода вы сможете оформить новую подписку. Также для перехода на уровень ниже вам нужно дождаться окончания действия текущей подписки.',
                paid_as_gift_non_recurrent: 'Подписка активирована по подарочной ссылке, поэтому после окончания оплаченного периода она не продлевается автоматически.',
                continue_subscription: 'Продолжить действие подписки',
                you_can_subscribe_again: 'Вы можете подписаться на этот уровень снова, при этом следующее списание произойдет {date}.',
                change_level: 'Сменить уровень',
                subscribe_again: 'Подписаться снова',
                payment_in_progress_please_wait: 'Идёт оплата. Пожалуйста, подождите...',
                after_the_transaction_you_will_be_automatically_redirected: 'После проведения транзакции вы будете автоматически перенаправлены на страницу с эффектами.',
                awaiting_payment: 'Ожидание оплаты',
                payment_success_redirect: 'Оплата прошла успешно, сейчас вы будете перенаправлены на страницу с эффектами.',
                payment_failed_try_again: 'Оплата не прошла, попробуйте еще раз.',
                try_again: 'Попытаться снова',
                this_level_is_higher_than_your_current_one: 'У вас есть действующая подписка на уровень "{name}", но вы можете перейти на этот уровень прямо сейчас.',
                this_level_is_lower_than_your_current_one: 'Этот уровень ниже вашего текущего. Вы можете запланировать переход на этот уровень. Переход будет произведен с {date}, а до этого времени вы продолжите пользоваться вашим текущим уровнем.',
                subscribe_to_this_level_now: 'Подпишитесь на этот уровень прямо сейчас.',
                this_level_is_next: 'Этот уровень ниже вашего текущего и вы запланировали переход на него с {date}.',
                you_have_planned_the_transition_to_the_level: 'Вы запланировали переход на уровень "{name}", но вы можете отменить переход.',
                cancel_transition: 'Отменить переход',
                are_you_sure_you_want_to_unsubscribe_from_level: 'Вы уверены что хотите отменить подписку на уровень "{name}"? В случае отмены списание средств прекратится, а подписка будет удалена после окончания действия оплаченного периода ({date}).',
                the_transition_will_also_be_canceled: 'Ранее вы запланировали переход на уровень "{name}", в случае отмены подписки переход также будет отменен.',
                payment_method_failed_subscribe_again: 'При попытке последнего списания произошла ошибка. Выберите уровень спонсорства и попробуйте еще раз, либо выберите другой способ оплаты.',
                is_expired: 'Срок действия подписки истек:',
                settings: 'Настройки подписки',
                free_upgrade_without_payment: 'Этот апгрейд полностью покрывается уже оплаченным периодом, поэтому будет выполнен без дополнительного платежа. Такой бесплатный апгрейд доступен только один раз для уже оплаченного периода.',
                free_upgrade_period: 'Новый оплаченный период на этом уровне: {months} мес. {days} дн.',
                credit_once_hint: 'Кредит за уже оплаченный период применяется один раз. После этого апгрейда прошлые платежи в вычет не попадут; при следующем апгрейде вычет будет считаться только из этого платежа и не превысит {amount} {currency}.',
            },
            generation: {
              page_header: 'Создание стикера с помощью нейронной сети',
              select_style: 'Выберите стиль',
              select_button: 'Выбрать',
              image_style: 'Стиль картинки',
              enter_text_here: 'Введите текст здесь',
              enter_text_to_create_sticker: 'Опишите, что вы хотите видеть на стикере (люди, предметы, персонажи, эмоции, цвета, окружение и т.д.).',
              create_button: 'Создать',
              loading_please_wait: 'Загрузка, пожалуйста, подождите...',
              attempt: 'Попытка:',
              select_this_sticker: 'Выбрать этот стикер',
              create_new_sticker: 'Создать новый стикер',
              style: 'Стиль',
              text_for_creation: 'Текст для создания стикера',
              you_can_select_this_sticker_and_send_it_to_the_stream_right_now: 'Вы можете выбрать этот стикер и отправить его в эфир прямо сейчас.',
              on_the_next_step_you_can_enter_your_name_and_message_text_if_you_wish: 'На следующем шаге Вы сможете ввести имя и текст сообщения, если пожелаете.',
              on_the_next_step_you_can_enter_your_name_if_you_wish: 'На следующем шаге Вы сможете ввести имя, если пожелаете.',
              on_the_next_step_you_can_enter_your_message_text_if_you_wish: 'На следующем шаге Вы сможете ввести текст сообщения, если пожелаете.',
              send_this_sticker: 'Отправить этот стикер',
              back_to_creation: 'Вернуться к созданию стикера',
              styles: {
                none: 'Без стиля',
                enhance: 'Улучшенный',
                anime: 'Аниме',
                photographic: 'Фотографический',
                digital_art: 'Цифровая графика',
                comic_book: 'Комикс',
                fantasy_art: 'Фэнтези',
                line_art: 'Штриховка',
                analog_film: 'Аналоговая пленка',
                neon_punk: 'Неон-панк',
                isometric: 'Изометрический',
                low_poly: 'Низкополигональный',
                origami: 'Оригами',
                modeling_compound: 'Лепка',
                cinematic: 'Кинематографический',
                three_d_model: '3D-модель',
                pixel_art: 'Пиксельная графика',
                tile_texture: 'Текстура плитки'
              },
              creation_in_progress: 'Производится создание стикера, пожалуйста, подождите...',
              error_while_creating_sticker: 'Произошла ошибка при создании стикера. Попробуйте еще раз.',
              image_not_available_try_to_reload_page: 'Изображение недоступно. Попробуйте перезагрузить страницу.',
              an_error_occurred_while_creating_the_sticker: 'Произошла ошибка при создании этого стикера. Попробуйте еще раз.',
              banned_words_found: 'В описании изображения найдены запрещенные символы или слова. Попробуйте другое описание.',
              an_unrecognized_error_occurred_while_creating_the_sticker: 'При создании стикера произошла нераспознанная ошибка. Изображение недоступно. Попробуйте перезагрузить страницу.',
              this_attempt_to_create_a_sticker_failed_try_again: 'Эта попытка создания стикера завершилась неудачно. Попробуйте еще раз.',
              error_while_posting_sticker: 'Произошла ошибка при отправке стикера. Попробуйте еще раз.',
              send_donation_without_sticker: 'Сделать пожертвование без отправки стикера',
              generation_disabled_globally: 'Создание стикеров временно отключено.',
            },
        },
    },
    pluralizationRules: {
        'ru': function(choice, choicesLength) {
            if (choice === 0) {
                return 0;
            }
            const teen = choice > 10 && choice < 20;
            const endsWithOne = choice % 10 === 1;
            if (choicesLength < 4) {
                return (!teen && endsWithOne) ? 1 : 2;
            }
            if (!teen && endsWithOne) {
                return 1;
            }
            if (!teen && choice % 10 >= 2 && choice % 10 <= 4) {
                return 2;
            }
            return (choicesLength < 4) ? 2 : 3;
        }
    }
});

var app = new Vue({
    i18n,
    router,
    store,
    render: h => h(App)
}).$mount("#app");

// Initialize store with server-injected config
const initPromise = store.dispatch('init', window.appConfig);

function signalReadyOnce() {
  try {
    const unhideRoot = () => {
      const hideEl = document.getElementById('dd-hide-app');
      if (hideEl && hideEl.parentNode) hideEl.parentNode.removeChild(hideEl);
      const spinnerEl = document.getElementById('dd-boot-spinner');
      if (spinnerEl && spinnerEl.parentNode) spinnerEl.parentNode.removeChild(spinnerEl);
    };
    if (window.__DD_READY__) {
      unhideRoot();
      return;
    }
    window.__DD_READY__ = true;
    document.dispatchEvent(new Event('dd:ready'));
    unhideRoot();
  } catch (e) {
    logWarn('Failed to signal dd:ready', e);
  }
}

// Wait until router has resolved the initial navigation before revealing the app.
// Otherwise, the legacy route/header can flash briefly before redirects to /wall/*.
router.onReady(() => {
  Promise.resolve(initPromise).then(() => {
    // Microtask to let Vue apply the latest route before we unhide.
    Promise.resolve().then(() => signalReadyOnce());
  });
});

if (window.appConfig?.visuals?.name) {
  document.title = window.appConfig.visuals.name;
}
